import { getTeamsSearch, type TeamDB } from "@aura-frontend/omnibus-az-client";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

type TeamDBWithParentName = TeamDB & { parent_name?: string };

export function useTeamNameOptions(organizationId: string | undefined) {
  // TODO: Replace this with a dedicated /orgs/{org_id}/team-names endpoint
  // Currently we fetch all teams and aggregate unique parent_name values.
  // A dedicated endpoint would be more efficient and eliminate the need for
  // paginating through all teams just to get team names.
  const { data, isLoading, error } = useQuery({
    queryKey: ["teamsSearch", organizationId],
    queryFn: async () => {
      const allTeams: TeamDB[] = [];
      let continueCursor: string | undefined;

      do {
        const result = await getTeamsSearch({
          path: {
            org_id: organizationId ? encodeURIComponent(organizationId) : "",
          },
          query: {
            visibilityGroup: organizationId ?? "",
            ...(continueCursor && { continue: continueCursor }),
          },
        });

        allTeams.push(...(result.data?.teams ?? []));
        continueCursor = result.data?.continue;
      } while (continueCursor);

      return allTeams;
    },
    enabled: !!organizationId,
  });

  const teamNameOptions = useMemo(() => {
    if (!data?.length) return [];

    const uniqueParentNames = new Set<string>();

    data.forEach((team) => {
      const typedTeam = team as TeamDBWithParentName;
      if (typedTeam.parent_name) {
        uniqueParentNames.add(typedTeam.parent_name);
      }
    });

    const sortedNames = Array.from(uniqueParentNames).sort();

    return sortedNames.map((name) => ({
      value: name,
      label: name,
    }));
  }, [data]);

  return {
    teamNameOptions,
    isLoadingTeamNames: isLoading,
    teamNamesError: error,
  } as const;
}
